import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import axiosInstant from "../../../api";
import { useUser } from "../../../hooks/useUser";
import { ExpertListingView } from "./expertListView";
import config from "../../../config";
import { IBrandExpert } from "./expertListView/constants";
import SingleProProfile from "../../../components/singlePro";
import { MainContainer } from "../../../components/ContentWrapper";
import { isBrand, isRMN } from "../../../utils";
import { useWorkspaceBrandId } from '../../../hooks/useWorkspaceBrandId';

export function ExpertsV2Listing() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user, role } = useUser();
  const [count, setCount] = useState(0);
  const [experts, setExperts] = useState<Array<IBrandExpert>>([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const append = useRef(false);

  const isFeatured = location.pathname.includes('/experts/featured');
  // Internal staff inside a client workspace read that brand's expert list
  // rather than the all-experts directory (TDC-1609).
  const workspaceBrandId = useWorkspaceBrandId();
  const isBrandOrRMN = isBrand(role) || isRMN(user) || !!workspaceBrandId;
  const brandId = workspaceBrandId || user?.brand?.[0]?.id;

  const fetchExperts = async (page = 1, searchPhrase = "", featured = false) => {
    const params: any = {
      limit: 20,
    };

    if (searchPhrase.length > 0) {
      params.search = searchPhrase;
    }

    const useBrandPros = !featured && isBrandOrRMN && brandId;

    if (useBrandPros) {
      params.page = page;
    } else {
      params.offset = (page - 1) * 20;
      if (featured) {
        params.featured = 'true';
      }
    }

    const endpoint = useBrandPros
      ? `${config.API_URL}/brands/${brandId}/pros`
      : `${config.API_URL}/users/pros`;

    setLoading(true);
    axiosInstant
      .get(endpoint, { params })
      .then((response) => {
        setCount(response.data.count);
        if (append.current)
          setExperts((prevExperts) => [...prevExperts, ...response.data.rows]);
        else setExperts(response.data.rows);
        append.current = false;
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // The detail view replaces the listing — don't fetch the list behind it.
    if (searchParams.get("proId")) return;
    const searchText = searchParams.get("search");
    setPageNo(1);
    append.current = false;
    fetchExperts(1, searchText || "", isFeatured);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isFeatured, user, role]);

  const handleLoadMore = () => {
    append.current = true;
    const nextPage = pageNo + 1;
    setPageNo(nextPage);
    fetchExperts(nextPage, "", isFeatured);
  };

  return searchParams.get("proId") ? (
    // The profile owns its own header bar + width — no MainContainer.
    <SingleProProfile isSharePage={false} />
  ) : (
    <MainContainer>
      <ExpertListingView
        count={count}
        experts={experts}
        loading={loading}
        loadMore={count > experts.length ? handleLoadMore : undefined}
        isFeatured={isFeatured}
      />
    </MainContainer>
  );
}
